import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, useAnimation } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import useIsMobile from '../../hooks/useIsMobile';

const slideUpFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ThemeToggleContainer = styled.div<{ isMobile: boolean }>`
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  cursor: pointer;
  margin-left: ${props => props.isMobile ? '-105.5px' : '-170px'}; /* Position above the 'I' in RiskMaster */
  margin-top: ${props => props.isMobile ? '95px' : '120px'};
  animation: ${slideUpFadeIn} 0.6s ease-out 1.0s forwards;
  opacity: 0;
`;

// Simple noise function for organic movement
const noise = (x: number, y: number): number => {
  let n = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
  return n - Math.floor(n);
};

const ThemeToggle: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const isMobile = useIsMobile();
  const controls = useAnimation();
  const animationRef = useRef<number>();
  const timeRef = useRef(0);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const animate = () => {
      if (!isHovering) {
        timeRef.current += 0.01;
        
        // Combine sine waves with noise for organic movement
        const time = timeRef.current;
        const noiseX = noise(time * 0.75, 0) * 2 - 1;
        const noiseY = noise(0, time * 0.75) * 2 - 1;
        
        const scale = 1 + Math.sin(time * 2) * 0.05;
        const borderRadius = `50% ${40 + Math.sin(time * 1.5) * 10}% ${50 + Math.sin(time * 2) * 15}% ${60 + Math.sin(time * 1.2) * 8}%`;
        const rotate = Math.sin(time * 0.8) * 10;
        const x = Math.sin(time * 1.2) * 0.5 + noiseX * 0.3;
        const y = Math.cos(time * 1.5) * 0.5 + noiseY * 0.3;

        controls.start({
          scale,
          borderRadius,
          rotate,
          x,
          y,
          transition: { duration: 0.1, ease: "linear" }
        });
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [controls, isHovering]);

  const handleHoverStart = () => {
    setIsHovering(true);
    // Stop all animations when hovering
    controls.stop();
  };

  const handleHoverEnd = () => {
    setIsHovering(false);
    // Smoothly transition back to the animated state
    controls.start({
      transition: { duration: 0.3, ease: "easeOut" }
    });
  };

  const buttonSize = isMobile ? 10 : 15;
  const hoverScale = isMobile ? 1.15 : 1.25;

  return (
    <ThemeToggleContainer isMobile={isMobile} onClick={toggleTheme}>
      <motion.button
        animate={controls}
        whileHover={{
          scale: hoverScale,
          boxShadow: theme === 'light' 
            ? '0 0 15px 5px rgba(0, 0, 0, 1), 0 0 25px 8px rgba(0, 0, 0, 0.6)' 
            : '0 0 15px 5px rgba(255, 255, 255, 1), 0 0 25px 8px rgba(255, 255, 255, 0.6)'
        }}
        onHoverStart={handleHoverStart}
        onHoverEnd={handleHoverEnd}
        style={{
          width: buttonSize,
          height: buttonSize,
          borderRadius: '50%',
          border: 'none',
          background: theme === 'light' ? '#000000' : '#FFFFFF',
          cursor: 'pointer',
          position: 'relative',
          boxShadow: theme === 'light' 
            ? '0 0 10px 3px rgba(0, 0, 0, 0.8), 0 0 20px 5px rgba(0, 0, 0, 0.4)' 
            : '0 0 10px 3px rgba(255, 255, 255, 0.8), 0 0 20px 5px rgba(255, 255, 255, 0.4)'
        }}
        aria-label="Toggle theme"
      />
    </ThemeToggleContainer>
  );
};

export default ThemeToggle;
